import _ from 'lodash';

import createModel from './lib/create_model';

const SOURCES = ['roleFeatures', 'gatekeeperFeatures', 'browserCapabilities'];
// TODO: remove ignore sources loaded once we get proper blocking on loading the app features for all sources.
const IGNORE_SOURCES_LOADED = ['demoMode'];

export const FeatureList = createModel({
  modelName: 'FeatureList',

  properties: {
    /* FEATURE FLAGS */

    // allow webRTC voice agents to be dialed while their client device is busy
    allowCallsWhileBusy: Boolean,

    // make "Best Match" the default sort in answer panel
    answerSearchBestMatch: Boolean,

    conversationTopicsFix: Boolean,

    debug: Boolean,

    // enable dedicated hero capabilities
    dedicatedHero: Boolean,

    demoMode: Boolean,

    // disable analytics tracking (e.g. segment), typically used for non-production orgs
    disableAnalytics: Boolean,

    // testFlag is used only for testing, as feature flags should generally be short-lived
    testFlag: Boolean,

    // can accept/make phones calls over direct dial
    directDial: Boolean,

    // suppress new version notifications
    disableNewVersionNotifications: Boolean,

    // ability to add inline images to emails
    emailInlineImages: Boolean,

    // use HTML to generate plain text
    emailPlainTextFromHtml: Boolean,

    // enable Instagram Business Messaging
    enableInstagramMessaging: Boolean,

    // enable outgoing chat over http
    enableOutgoingChatHttp: Boolean,

    // fetches active call from supernova when a webRTC voice agent are dialed. This is address issues where agents do not receive the offered call routing event
    fetchActiveCallWhenDialed: Boolean,

    // can create Facebook message composition
    fbMessenger: Boolean,

    // show the gladly chat widget to admins
    gladlyOnGladlyChat: Boolean,

    // enables logging of offered call events to amplitude
    offeredCallLogging: Boolean,

    // show the IVR reporting tab on the Reporting page
    ivrReporting: Boolean,

    // whether the browser supports and the user allowed the microphone
    microphone: Boolean,

    // prevent calls from being auto declined when agent is missing voice capabilities
    preventAutoRejectCalls: Boolean,

    // enables admins to prioritize chat over other messaging
    prioritizationUI: Boolean,

    // in development: recent customers
    recentCustomers: Boolean,

    // in development: ability for admins to redact attachments
    redactAttachments: Boolean,

    // can accept/make phone calls over SIP
    sip: Boolean,

    // can send/receive sms messages
    sms: Boolean,

    // can add attachment to sms composition
    smsOutgoingAttachments: Boolean,

    // enables CTI integration with third party telephony systems
    telephonyApi: Boolean,

    // enables conversation and customer search over http
    universalSearchHttp: Boolean,

    // enables user survey on away page
    userSurvey: Boolean,

    // Enable endpoint cache in telephony service
    useEndpointCache: Boolean,

    // enables warm transfer to inbox UI
    warmTransferToInbox: Boolean,

    // enable the switch from fb sdk v3.3 to v9.0
    upgradeFacebookSDK: Boolean,

    // can accept/make phones calls over webRTC
    webRTC: Boolean,

    /* ROLE FEATURES */

    // - can navigate to admin settings
    adminSettings: Boolean,

    // - can communicate with customers
    externalAgentActions: Boolean,

    // - can be routed conversations
    // - can work on conversations
    // - can search across all conversations
    // - can suggest new content
    internalAgentActions: Boolean,

    // can create and update campaigns
    campaignsManagement: Boolean,

    // can create and update channels
    channelManagement: Boolean,

    // can edit assignee (dedicated hero) for customer
    customerAssigneeManagement: Boolean,

    // can delete communication attachments
    manageAttachments: Boolean,

    // can download and delete phone call and voicemail recordings
    manageVoiceRecordings: Boolean,

    // can view group inboxes (in the dashboard)
    groupInboxes: Boolean,

    // - can create and edit routing groups
    // - can assign agents to routing groups
    inboxManagement: Boolean,

    // - can list, add, delete personal API tokens
    // - can list, add, delete webhooks
    devManagement: Boolean,

    // can view and edit integrations (eg. customer lookup)
    integrationsManagement: Boolean,

    // can edit answers/variables
    kbManagement: Boolean,

    // enables new multi-source UI in the AD
    multisourceLookup: Boolean,

    // can view the multi-source lookup admin pages
    multisourceLookupAdmin: Boolean,

    // remove response prompt and clear reply headers for emails for closed conversations
    newReplyWorkflow: Boolean,

    // can edit rules
    rulesManagement: Boolean,

    // can view the existing users
    // can edit teams
    teamManagement: Boolean,

    // can create & update topics
    topicManagement: Boolean,

    // userManagement enables the following:
    // - can create and edit users (agents only)
    // - can trigger password reset for users (agents only)
    // - can edit agent channels
    userManagement: Boolean,

    // can see all routingGroup inboxes
    viewAllGroupInboxes: Boolean,

    // can view liveboards
    viewLiveboards: Boolean,

    // can view reports
    viewReports: Boolean,

    // whether the user has logged in using SSO
    sso: Boolean,

    // can view webhooks admin page
    webhooks: Boolean,
  },
});

const AppFeatures = createModel({
  modelName: 'AppFeatures',

  properties: {
    roleFeatures: FeatureList,
    gatekeeperFeatures: FeatureList,
    browserCapabilities: FeatureList,
  },

  replace(source, features) {
    const featureList = new FeatureList(features);
    const newAttrs = _.merge({}, this, { [source]: featureList });
    return new AppFeatures(newAttrs);
  },

  sourcesAreLoaded() {
    return _.chain(this)
      .pick(SOURCES)
      .values()
      .every()
      .value();
  },

  isEnabled(feature) {
    if (this.sourcesAreLoaded() || _.includes(IGNORE_SOURCES_LOADED, feature)) {
      return (
        _.some(SOURCES, source => this[source] && this[source][feature] === true) &&
        _.every(SOURCES, source => this[source] && this[source][feature] !== false)
      );
    }

    return false;
  },

  statics: {
    create(attrs) {
      return new this(attrs);
    },
    SOURCES,
  },
});

export default AppFeatures;



// WEBPACK FOOTER //
// ./src/scripts/domain/models/app_features.js